const PI2 = Math.PI * 2;

// --- smoothstep ---
export function smoothstep(min, max, value) {
	const x = clamp((value - min) / (max - min), 0, 1);
	return x * x * (3 - 2 * x);
}

// ------ map -------
export function clamp(value, min = 0, max = 1) {
	return Math.min(Math.max(value, min), max);
}
export function map(value, start1, stop1, start2, stop2) {
	return start2 + (stop2 - start2) * ((value - start1) / (stop1 - start1));
}
export function clampedMap(value, start1, stop1, start2, stop2) {
	const v = start2 + (stop2 - start2) * ((value - start1) / (stop1 - start1));
	let min = start2;
	let max = stop2;
	if (start2 > stop2) { min = stop2; max = start2 }
	return Math.max(min, Math.min(max, v));
}
export function norm(value, min = 0, max = 1) {
	return (value - min) / (max - min);
}
export function clampedNorm(value, min = 0, max = 1) {
	return Math.max(0, Math.min(1, (value - min) / (max - min)));
}
// 0;0.5;1 -> 0;1;0
export function yoyo(v) {
	return 1 - Math.abs(2 * v - 1);
}

// ------ lerp -------
export const mix = lerp;
export const mixPrecise = lerpPrecise;
export function lerp(start, end, t) {
	return start * (1 - t) + end * t;
}
export function lerpPrecise(start, end, t, limit = 0.001) {
	const v = start * (1 - t) + end * t;
	return Math.abs(end - v) < limit ? end : v;
}
export function damp(a, b, smoothing, dt) {
	return lerp(a, b, 1 - Math.exp(-smoothing * 0.05 * dt));
}
export function dampPrecise(a, b, smoothing, dt, limit) {
	return lerpPrecise(a, b, 1 - Math.exp(-smoothing * 0.05 * dt), limit);
}
export function angleDist(a0, a1) {
	let da = (a1 - a0) % PI2;
	return 2 * da % PI2 - da;
}
export function lerpAngle(a0, a1, t) {
	return a0 + angleDist(a0, a1) * t;
}
export function dampAngle(a, b, smoothing, dt) {
	return lerpAngle(a, b, 1 - Math.exp(-smoothing * 0.05 * dt));
}

// ------ distance -------
export function sqdist(x1, y1, x2, y2) {
	return (x2 - x1) * (x2 - x1) + (y2 - y1) * (y2 - y1);
}

export function dist(x1, y1, x2, y2) {
	return Math.sqrt(sqdist(x1, y1, x2, y2));
}


// ------ mod -------
export function mod(dividend, divisor) {
	return ((dividend % divisor) + divisor) % divisor;
}

export function wrap(min, max, value) {
	const range = max - min;
	return min + (((( value - min ) % range) + range) % range);
}


// ------ stats -------
export const mean = average;

export function average(values) {
	let val = 0;
	const len = values.length;
	for (let i = 0; i < len; i++) val += values[ i ];
	return val / len;
}

export function median(values = []) {
	const numbers = values.slice(0).sort((a, b) => a - b);
	const middle = Math.floor(numbers.length / 2);
	const isEven = numbers.length % 2 === 0;
	return isEven ? (numbers[ middle ] + numbers[ middle - 1 ]) / 2 : numbers[ middle ];
}

export function createMovingAverage(maxSize = 6) {
	const buffer = new Float64Array(maxSize);
	const api = { size: 0, value: 0, push, reset };
	let sum, index;
	sum = index = 0;
	return api;

	function push(value) {
		if (api.size < maxSize) api.size++;
		index = (index + 1) % maxSize;
		const prevValue = buffer[ index ];
		buffer[ index ] = value;
		sum += value - prevValue;
		return api.value = sum / api.size;
	}

	function reset() {
		api.size = sum = index = 0;
		for (let i = 0; i < maxSize; i++) buffer[ i ] = 0;
	}
}


// ------ noise -------
// https://github.com/joshforisha/fast-simplex-noise-js/blob/main/src/2d.ts
const _G2 = (3.0 - Math.sqrt(3.0)) / 6.0;

const _Grad = [
	[ 1, 1 ], [ -1, 1 ], [ 1, -1 ], [ -1, -1 ],
	[ 1, 0 ], [ -1, 0 ], [ 1, 0 ], [ -1, 0 ],
	[ 0, 1 ], [ 0, -1 ], [ 0, 1 ], [ 0, -1 ],
];

export function createNoise2D(random = Math.random) {
	const p = new Uint8Array(256);
	for (let i = 0; i < 256; i++) p[ i ] = i;
	let n, q;
	for (let i = 255; i > 0; i--) {
		n = Math.floor((i + 1) * random());
		q = p[ i ]; p[ i ] = p[ n ]; p[ n ] = q;
	}
	const perm = new Uint8Array(512);
	const permMod12 = new Uint8Array(512);
	for (let i = 0; i < 512; i++) {
		perm[ i ] = p[ i & 255 ];
		permMod12[ i ] = perm[ i ] % 12;
	}
	return (x, y) => {
		const s = (x + y) * 0.5 * (Math.sqrt(3.0) - 1.0);
		const i = Math.floor(x + s);
		const j = Math.floor(y + s);
		const t = (i + j) * _G2;
		const x0 = x - (i - t); const y0 = y - (j - t);
		const i1 = x0 > y0 ? 1 : 0; const j1 = x0 > y0 ? 0 : 1;
		const x1 = x0 - i1 + _G2; const y1 = y0 - j1 + _G2;
		const x2 = x0 - 1.0 + 2.0 * _G2; const y2 = y0 - 1.0 + 2.0 * _G2;
		const ii = i & 255; const jj = j & 255;
		const g0 = _Grad[ permMod12[ ii + perm[ jj ] ] ];
		const g1 = _Grad[ permMod12[ ii + i1 + perm[ jj + j1 ] ] ];
		const g2 = _Grad[ permMod12[ ii + 1 + perm[ jj + 1 ] ] ];
		const t0 = 0.5 - x0 * x0 - y0 * y0;
		const n0 = t0 < 0 ? 0.0 : Math.pow(t0, 4) * (g0[ 0 ] * x0 + g0[ 1 ] * y0);
		const t1 = 0.5 - x1 * x1 - y1 * y1;
		const n1 = t1 < 0 ? 0.0 : Math.pow(t1, 4) * (g1[ 0 ] * x1 + g1[ 1 ] * y1);
		const t2 = 0.5 - x2 * x2 - y2 * y2;
		const n2 = t2 < 0 ? 0.0 : Math.pow(t2, 4) * (g2[ 0 ] * x2 + g2[ 1 ] * y2);
		return 70.14805770653952 * (n0 + n1 + n2);
	};
}


// ------ prng -------
// https://github.com/bryc/code/blob/master/jshash/PRNGs.md

const _DEFAULT_SEED = 'MM' + (Math.floor(Math.random() * 1e13) + Date.now());

export function xmur3Hash(str) {
	let h = 1779033703 ^ str.length;
	for (let i = 0; i < str.length; i++) {
		h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
		h = h << 13 | h >>> 19;
	}
	return function() {
		h = Math.imul(h ^ h >>> 16, 2246822507);
		h = Math.imul(h ^ h >>> 13, 3266489909);
		return (h ^= h >>> 16) >>> 0;
	};
}

export function mulberry32Prng(a) {
	if (typeof a === 'function') a = a();
	return function() {
		a |= 0; a = a + 0x6D2B79F5 | 0;
		let t = Math.imul(a ^ a >>> 15, 1 | a);
		t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
		return ((t ^ t >>> 14) >>> 0) / 4294967296;
	};
}

export function stringToSeed(str) { return xmur3Hash(str + '')(); }
export function stringToNumber(str) { return mulberry32Prng(xmur3Hash(str + '')())(); }

function createPrng(seedStr = _DEFAULT_SEED, generator = mulberry32Prng) {
	let nextFloat;
	setSeed(seedStr);
	return { setSeed, random, randomFloat, randomInt, tossCoin, weightedChoices };

	function setSeed(s) { nextFloat = generator(xmur3Hash(s + '')); nextFloat(); }
	function random() { return nextFloat(); }
	function tossCoin(probability = 0.5) { return nextFloat() < probability; }
	function randomFloat(min = 0, max = 1) { return nextFloat() * (max - min) + min; }
	function randomInt(min = 0, max = 100) { return Math.floor(nextFloat() * (max - min + 1)) + min; }
	function weightedChoices(els) {
		let total = 0, sum = 0;
		for (const el of els) total += el[ 0 ];
		for (const el of els) el[ 3 ] = (sum += el[ 0 ] / total);
		return function(value) {
			if (value == null) value = nextFloat();
			for (let i = 0; i < els.length; i++) if (value < els[ i ][ 3 ]) return els[ i ][ 1 ];
			return els[ els.length - 1 ];
		};
	}
}

export const prng = createPrng(_DEFAULT_SEED);
prng.create = createPrng;


// ------ valuesMapper -------
// Linear interpolation between multiple values in an array,
// This is kind of a js version of the scss `fluidSize` mixin
export function valuesMapper(arr) {
	const [ minIn, minOut ] = arr[ 0 ];
	const [ maxIn, maxOut ] = arr[ arr.length - 1 ];
	let len = arr.length;
	let lastValue = null;
	let lastReturn = 0;
	let lastBound = -1;

	return function valuesMap(current) {
		if (lastValue === current) return lastReturn;
		lastValue = current;
		if (current <= minIn) return (lastBound = 0, lastReturn = minOut);
		if (current >= maxIn) return (lastBound = len - 1, lastReturn = maxOut);
		for (let i = (lastBound !== -1 ? -1 : 0); i < len; i++) {
			let idx = i === -1 ? lastBound : i;
			const bound = arr[ idx ];
			const nextBound = arr[ idx + 1 ];
			if (current >= bound[ 0 ] && current < nextBound[ 0 ]) {
				lastBound = idx;
				return (lastReturn = map(
					current,
					bound[ 0 ],
					nextBound[ 0 ],
					bound[ 1 ],
					nextBound[ 1 ]
				));
			}
		}
	};
}
